import React, { useState, useEffect } from 'react';
import { Waiter, CreateWaiterData } from '../../types';
import { adminWaitersService } from '../../services/adminWaiters';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import ErrorModal from '../../components/ErrorModal';
import { ApiError } from '../../services/http';
import { userStorage } from '../../services/userStorage';

const WaitersPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  
  const [waiters, setWaiters] = useState<Waiter[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newWaiterEmail, setNewWaiterEmail] = useState('');
  const [creatingWaiter, setCreatingWaiter] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });

  // Cargar meseros
  const loadWaiters = async () => {
    if (!user?.uid || !selectedRestaurantId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const waitersData = await adminWaitersService.getWaiters(user.uid, selectedRestaurantId);
      
      // Verificar si hay duplicados y loggear para debug
      const waiterIds = waitersData.map(w => w.id);
      const uniqueIds = [...new Set(waiterIds)];
      
      if (waiterIds.length !== uniqueIds.length) {
        console.log('⚠️ Se encontraron meseros duplicados por ID:', {
          total: waitersData.length,
          unique: uniqueIds.length,
          duplicateIds: waiterIds.filter((id, index) => waiterIds.indexOf(id) !== index)
        });
        
        // Si hay duplicados por ID, usar email como identificador único
        const uniqueWaiters = waitersData.filter((waiter, index, self) => 
          index === self.findIndex(w => w.email === waiter.email)
        );
        
        console.log('📊 Meseros cargados (usando email como ID único):', {
          original: waitersData.length,
          filtered: uniqueWaiters.length,
          waiters: uniqueWaiters.map(w => ({ id: w.id, email: w.email, status: w.status }))
        });
        
        setWaiters(uniqueWaiters);
      } else {
        // Si no hay duplicados, usar normalmente
        console.log('📊 Meseros cargados:', {
          total: waitersData.length,
          waiters: waitersData.map(w => ({ id: w.id, email: w.email, status: w.status }))
        });
        
        setWaiters(waitersData);
      }
    } catch (error) {
      console.error('❌ Error cargando meseros:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudieron cargar los meseros', message: error.message });
      } else {
        setError('Error al cargar los meseros');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWaiters();
  }, [user?.uid, selectedRestaurantId]);

  // Crear nuevo mesero
  const handleCreateWaiter = async () => {
    if (!user?.uid || !selectedRestaurantId || !newWaiterEmail.trim()) {
      setErrorModal({ open: true, title: 'Datos faltantes', message: 'Por favor ingresa un email válido o crea un restaurante primero' });
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newWaiterEmail)) {
      setErrorModal({ open: true, title: 'Email inválido', message: 'Por favor ingresa un email válido' });
      return;
    }

    try {
      setCreatingWaiter(true);
      setError(null);
      
      const waiterData: CreateWaiterData = {
        email: newWaiterEmail.trim(),
        restaurantId: selectedRestaurantId
      };

      await adminWaitersService.createWaiter(user.uid, waiterData);
      
      // Limpiar formulario y cerrar modal
      setNewWaiterEmail('');
      setShowAddModal(false);
      
      // Recargar meseros
      await loadWaiters();
      
    } catch (error: any) {
      console.error('❌ Error creando mesero:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudo crear el mesero', message: error.message });
      } else {
        setError(error.message || 'Error al crear el mesero');
      }
    } finally {
      setCreatingWaiter(false);
    }
  };

  // Cambiar estado del mesero
  const handleToggleWaiterStatus = async (waiter: Waiter) => {
    if (!user?.uid || !selectedRestaurantId) return;
    
    if (waiter.status === 'pending') {
      setErrorModal({ open: true, title: 'Acción no permitida', message: 'No se puede cambiar el estado de un mesero pendiente' });
      return;
    }

    try {
      // Determinar el nuevo status basado en el status actual
      let newStatus: 'active' | 'inactive';
      if (waiter.status === 'active' || (waiter.status && waiter.status.startsWith('active_shift:'))) {
        newStatus = 'inactive';
      } else {
        newStatus = 'active';
      }
      
      await adminWaitersService.updateWaiter(user.uid, selectedRestaurantId, waiter.id, { status: newStatus });
      
      // Actualizar estado local
      setWaiters(prev => prev.map(w => 
        w.id === waiter.id ? { ...w, status: newStatus } : w
      ));
      
      console.log(`✅ Estado del mesero ${waiter.name} cambiado a ${newStatus}`);
    } catch (error) {
      console.error('❌ Error cambiando estado del mesero:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudo cambiar el estado', message: error.message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'Error al cambiar el estado del mesero' });
      }
    }
  };

  // Obtener texto del estado
  const getStatusText = (status: string) => {
    // Manejar status de turno del backend
    if (status && typeof status === 'string') {
      if (status.startsWith('active_shift:')) {
        return 'En turno';
      } else if (status.startsWith('inactive_shift:')) {
        return 'Fuera de turno';
      }
    }
    
    // Manejar otros status
    switch (status) {
      case 'active': return 'Activo';
      case 'inactive': return 'Inactivo';
      case 'pending': return 'Pendiente';
      case 'suspended': return 'Suspendido';
      default: return status;
    }
  };

  // Obtener color del status
  const getStatusColor = (status: string) => {
    // Manejar status de turno del backend
    if (status && typeof status === 'string') {
      if (status.startsWith('active_shift:')) {
        return 'from-green-400 to-green-600';
      } else if (status.startsWith('inactive_shift:')) {
        return 'from-gray-400 to-gray-600';
      }
    }
    
    // Manejar otros status
    switch (status) {
      case 'active': return 'from-green-400 to-green-600';
      case 'inactive': return 'from-gray-400 to-gray-600';
      case 'pending': return 'from-yellow-400 to-yellow-600';
      case 'suspended': return 'from-red-400 to-red-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-[#22B6B2]/20 border-t-[#22B6B2] rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-t-[#2C4358] rounded-full animate-spin" style={{animationDelay: '0.5s'}}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 bg-[#e9ecef] min-h-screen">
      {/* Header futurista */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-4 sm:gap-0">
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-[#f7ebd6] text-[#2C4358] px-6 sm:px-8 py-3 sm:py-4 rounded-2xl flex items-center gap-3 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-black text-sm sm:text-base relative overflow-hidden group border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0] hover:bg-[#f0e0c0]"
        >
          {/* Efecto de brillo */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          <span className="relative z-10 text-lg sm:text-xl">➕</span>
          <span className="relative z-10">Agregar Mesero</span>
        </button>
      </div>

      {/* Error futurista */}
      {error && (
        <div className="bg-gradient-to-r from-red-500/10 to-red-600/10 border-2 border-red-500/30 backdrop-blur-sm rounded-2xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <span className="text-2xl">❌</span>
            <span className="text-red-600 font-bold text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Lista de meseros futurista */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        {waiters.length === 0 ? (
          <div className="col-span-full text-center py-12 sm:py-16">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/20 to-[#2C4358]/20 rounded-full blur-2xl"></div>
              <div className="relative w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-[#22B6B2]/30 to-[#2C4358]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl sm:text-4xl">👥</span>
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-[#2C4358] mb-3">No hay meseros</h3>
            <p className="text-[#85929e] font-bold text-sm sm:text-base max-w-md mx-auto">
              Comienza construyendo tu equipo de servicio agregando tu primer mesero.
            </p>
          </div>
        ) : (
          waiters.map((waiter, index) => {
            const isPlanLimited = waiter.status === 'plan_limited' || (typeof waiter.status === 'string' && waiter.status.toLowerCase().includes('plan'));
            const canPromptPay = isPlanLimited && user && (userStorage.isOwner() || userStorage.isManager());
            return (
            <div key={`${waiter.id || waiter.email}-${waiter.status}-${index}`} className={`relative group ${isPlanLimited ? 'opacity-60 saturate-50 select-none' : ''}`}>
              {/* Efecto de fondo */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/10 to-[#2C4358]/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative bg-white/90 backdrop-blur-sm border-2 border-[#22B6B2]/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 flex flex-col gap-2 sm:gap-3 lg:gap-4 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 overflow-hidden">
                {/* Barra de estado superior */}
                <div className={`h-1 w-full rounded-full ${isPlanLimited ? 'bg-gradient-to-r from-red-400 to-red-600' : `bg-gradient-to-r ${getStatusColor(waiter.status)}`}`}></div>
                
                <div className="flex items-center justify-between gap-2 sm:gap-3 lg:gap-4">
                  <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 min-w-0 flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-[#22B6B2]/20 to-[#2C4358]/20 rounded-full flex items-center justify-center border-2 border-[#22B6B2]/30">
                        <span className="text-lg sm:text-xl lg:text-2xl">👤</span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm sm:text-base lg:text-lg font-black text-[#2C4358] truncate">
                        {waiter.fullName || waiter.name || 'Sin nombre'}
                      </h3>
                      <p className="text-xs sm:text-sm text-[#85929e] font-bold truncate">{waiter.email}</p>
                      {waiter.phone && (
                        <p className="text-xs sm:text-sm text-[#85929e] font-bold truncate">{waiter.phone}</p>
                      )}
                    </div>
                  </div>
                   <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-black flex-shrink-0 ${isPlanLimited ? 'bg-gradient-to-r from-red-400 to-red-600 text-white' : `bg-gradient-to-r ${getStatusColor(waiter.status)} text-white`}`}>
                    {isPlanLimited ? 'Limitado por plan' : getStatusText(waiter.status)}
                  </span>
                </div>
                
                <div className="flex flex-wrap gap-2 sm:gap-3 items-center justify-between mt-2">
                  <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                    <button
                      onClick={() => handleToggleWaiterStatus(waiter)}
                      className={`flex-1 sm:flex-none px-2 sm:px-3 py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-black transition-all duration-300 transform hover:scale-105 ${(waiter.status === 'active' || (waiter.status && waiter.status.startsWith('active_shift:'))) ? 'bg-gradient-to-r from-gray-400 to-gray-600 text-white hover:from-gray-500 hover:to-gray-700' : 'bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white hover:from-[#1a8f8c] hover:to-[#1e2f3f]'}`}
                      disabled={isPlanLimited}
                    >
                      <span className="hidden sm:inline">{isPlanLimited ? '🔒 Limitado' : ((waiter.status === 'active' || (waiter.status && waiter.status.startsWith('active_shift:'))) ? '⏸️ Desactivar' : '▶️ Activar')}</span>
                      <span className="sm:hidden">{isPlanLimited ? '🔒' : ((waiter.status === 'active' || (waiter.status && waiter.status.startsWith('active_shift:'))) ? '⏸️' : '▶️')}</span>
                    </button>
                  </div>
                </div>
                {canPromptPay && (
                  <div className="absolute inset-0 flex items-center justify-center p-3">
                    <div className="bg-white/95 backdrop-blur-md border border-[#22B6B2]/30 rounded-2xl shadow-2xl px-4 py-3 text-center">
                      <div className="text-sm font-bold text-[#2C4358] mb-2">Limitado por plan</div>
                      <button
                        onClick={() => window.location.href = '/pricing'}
                        className="px-3 py-2 rounded-xl bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white text-sm font-bold hover:opacity-90 transition"
                      >
                        Actualizar plan
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )})
        )}
      </div>

      {/* Modal futurista para agregar mesero */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4">
          <div className="bg-white/90 backdrop-blur-md border-2 border-[#22B6B2]/20 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl w-full max-w-xs sm:max-w-md mx-2 sm:mx-4 max-h-[90vh] flex flex-col overflow-hidden">
            {/* Fondo animado sutil */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/5 via-[#2C4358]/5 to-[#85929e]/5 animate-pulse"></div>
            {/* Header fijo */}
            <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 backdrop-blur-md border-b border-[#22B6B2]/20 px-4 sm:px-6 py-4 sm:py-6 rounded-t-2xl sm:rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">👤</span>
                  <h2 className="text-lg sm:text-xl font-black text-[#2C4358]">Agregar Mesero</h2>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewWaiterEmail('');
                    setError(null);
                  }}
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 hover:from-[#22B6B2]/20 hover:to-[#2C4358]/20 text-[#2C4358] hover:text-[#22B6B2] text-lg sm:text-xl transition-all duration-300 border-2 border-[#22B6B2]/30 hover:border-[#22B6B2]/50 hover:scale-110 group"
                >
                  <span className="group-hover:rotate-90 transition-transform duration-300">✕</span>
                </button>
              </div>
            </div>
            {/* Body scrolleable con borde redondeado */}
            <div className="overflow-hidden rounded-b-2xl sm:rounded-b-3xl flex-1 flex flex-col">
              <div className="relative z-10 p-4 sm:p-6 overflow-y-auto h-full">

                {/* Formulario */}
                <div className="mb-6">
                  <label className="block text-sm font-black text-[#2C4358] mb-3">📧 Email del mesero</label>
                  <input
                    type="email"
                    value={newWaiterEmail}
                    onChange={(e) => setNewWaiterEmail(e.target.value)}
                    placeholder="mesero@ejemplo.com"
                    className="w-full px-4 py-3 border-2 border-[#22B6B2]/30 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#22B6B2]/30 focus:border-[#22B6B2] text-sm font-bold bg-white/80 backdrop-blur-sm transition-all duration-300"
                    disabled={creatingWaiter}
                  />
                </div>

                {/* Información */}
                <div className="bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 border-2 border-[#22B6B2]/20 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-lg">📨</span>
                      <div className="text-xs sm:text-sm text-[#85929e] font-bold">
                        El sistema reconocerá automáticamente al mesero cuando acceda a la plataforma.
                      </div>
                  </div>
                </div>

                {/* Botones */}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setNewWaiterEmail('');
                      setError(null);
                    }}
                    className="px-4 py-3 text-[#85929e] hover:text-[#2C4358] transition-all duration-300 text-sm font-black rounded-xl hover:bg-[#22B6B2]/10"
                    disabled={creatingWaiter}
                  >
                    ❌ Cancelar
                  </button>
                  <button
                    onClick={handleCreateWaiter}
                    disabled={creatingWaiter || !newWaiterEmail.trim()}
                    className="bg-gradient-to-r from-[#22B6B2] to-[#2C4358] hover:from-[#1a8f8c] hover:to-[#1e2f3f] disabled:from-gray-400 disabled:to-gray-600 text-white px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-black text-sm relative overflow-hidden group"
                  >
                    {/* Efecto de brillo */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <span className="relative z-10">
                      {creatingWaiter ? '⏳ Agregando...' : '📤 Agregar'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ErrorModal isOpen={errorModal.open} title={errorModal.title} message={errorModal.message} details={errorModal.details} onClose={() => setErrorModal({ open: false, message: '' })} />
    </div>
  );
};

export default WaitersPage; 